import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CalendarDays, PlusCircle, Pin, PinOff } from 'lucide-react';
import { formatDateKey, getDayNameFromDate, getDayNameSpanish, isValidDate, getWeekDays } from '@/lib/dateUtils';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { useToast } from "@/components/ui/use-toast";
import { ICONS } from '@/config/constants';

export function AddDogDialog({ onAddDog, currentDate, triggerId = "add-dog-dialog-trigger-default" }) {
  const [dogName, setDogName] = useState('');
  const [dogWhatsApp, setDogWhatsApp] = useState('');
  const [dogLocationUrl, setDogLocationUrl] = useState('');
  const [dogCount, setDogCount] = useState('1');
  const [isFixed, setIsFixed] = useState(true);
  const [selectedDateKeysForAdd, setSelectedDateKeysForAdd] = useState([]);
  const [pickerDate, setPickerDate] = useState(currentDate || new Date());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isDialogOpen) {
      console.log("[ADD_DOG_DIALOG] Mobile-optimized dialog opened");
      setDogName('');
      setDogWhatsApp('');
      setDogLocationUrl('');
      setDogCount('1');
      setIsFixed(true);
      const initialPickerDate = currentDate && isValidDate(new Date(currentDate)) ? new Date(currentDate) : new Date();
      setPickerDate(initialPickerDate);
      setSelectedDateKeysForAdd([formatDateKey(initialPickerDate)]);
    }
  }, [isDialogOpen, currentDate]);

  const handleDateToggle = (dateKey) => {
    setSelectedDateKeysForAdd((prev) =>
      prev.includes(dateKey) ? prev.filter((d) => d !== dateKey) : [...prev, dateKey]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!dogName.trim()) {
      toast({
        title: "Error",
        description: "El nombre del perro no puede estar vacío.",
        variant: "destructive",
      });
      return;
    }
    if (selectedDateKeysForAdd.length === 0) {
      toast({
        title: "Error",
        description: "Debes seleccionar al menos un día.",
        variant: "destructive",
      });
      return;
    }
    const success = onAddDog(dogName.trim(), dogWhatsApp.trim(), selectedDateKeysForAdd, parseInt(dogCount, 10) || 1, isFixed, dogLocationUrl.trim());
    if (success) {
      setIsDialogOpen(false);
    }
  };

  const weekDaysForPicker = getWeekDays(pickerDate);

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <button id={triggerId} className="hidden"></button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px] bg-card max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-foreground">Añadir Nuevo Cliente</DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto pr-2 -mr-2">
          <form id="add-dog-form" onSubmit={handleSubmit} className="grid gap-6 py-4">
          <div className="grid gap-2">
            <Label htmlFor="dog-name" className="text-muted-foreground">Nombre del Cliente</Label>
            <Input
              id="dog-name"
              value={dogName}
              onChange={(e) => setDogName(e.target.value)}
              className="bg-background border-border focus:ring-primary"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="dog-whatsapp" className="text-muted-foreground">WhatsApp (Opcional)</Label>
            <Input
              id="dog-whatsapp"
              value={dogWhatsApp}
              onChange={(e) => setDogWhatsApp(e.target.value)}
              placeholder="Enlace del grupo de WhatsApp"
              className="bg-background border-border focus:ring-primary"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="dog-location" className="text-muted-foreground">Localización de Google Maps (Opcional)</Label>
            <Input
              id="dog-location"
              value={dogLocationUrl}
              onChange={(e) => setDogLocationUrl(e.target.value)}
              placeholder="URL de Google Maps"
              className="bg-background border-border focus:ring-primary"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="dog-count" className="text-muted-foreground">Cantidad</Label>
            <Input
              id="dog-count"
              type="number"
              min="1"
              value={dogCount}
              onChange={(e) => setDogCount(e.target.value)}
              className="bg-background border-border focus:ring-primary"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch id="dog-fixed" checked={isFixed} onCheckedChange={setIsFixed} />
             <Label htmlFor="dog-fixed" className="text-muted-foreground flex items-center">
               {isFixed ? <Pin className="mr-2 h-4 w-4 text-primary" /> : <PinOff className="mr-2 h-4 w-4" />}
               Cliente fijo (se repite semanalmente)
             </Label>
          </div>

          <div>
             <div className="flex justify-between items-center mb-2">
               <Label className="text-muted-foreground">Días del Cliente</Label>
               <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="text-xs">
                    <ICONS.CalendarPlus className="mr-1 h-3 w-3" /> Cambiar Semana
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-border">
                  <Calendar
                    mode="single"
                    selected={pickerDate}
                    onSelect={(date) => {
                      if(date && isValidDate(date)) setPickerDate(date);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {weekDaysForPicker.map((date) => {
                const dateKey = formatDateKey(date);
                const dayName = getDayNameSpanish(date);
                const isSelected = selectedDateKeysForAdd.includes(dateKey);
                return (
                  <Button
                    key={dateKey}
                    type="button"
                    onClick={() => handleDateToggle(dateKey)}
                    className={cn(
                      "w-full h-auto py-2 border transition-colors",
                      isSelected
                        ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                        : "bg-card text-foreground border-border hover:bg-muted"
                    )}
                  >
                    <div className="flex flex-col items-center">
                      <span>{dayName}</span>
                      <span className="text-xs opacity-75">{date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        </form>
        </div>

        <DialogFooter className="pt-4 shrink-0 border-t border-border">
          <DialogClose asChild>
            <Button type="button" variant="outline" className="hover:bg-muted">Cancelar</Button>
          </DialogClose>
           <Button type="submit" form="add-dog-form" className="bg-primary text-primary-foreground hover:bg-primary/90">
             <PlusCircle className="mr-2 h-4 w-4" />
             Añadir Cliente
           </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
